/**
 * useInFlightTriggers — Phase 30.1.4
 *
 * Polls /api/assessments/triggers?status=pending,pulling_video,analyzing,
 * pending_review,pending_section_review and returns the set of triggers
 * actively moving through the pipeline. Drives the "In progress" strip on
 * AssessmentsPage.
 *
 * Adaptive polling (m3 fix): 5s when the strip has rows, 30s when empty —
 * mirrors the useTriggerDetail pattern at useAssessments.ts:207-211 to avoid
 * a permanent 5s poll cost when the user has no in-flight work.
 *
 * Backend filter (M2 fix): the `?status=` query param is added to the
 * trigger list endpoint at apps/api/src/routes/assessments.ts (Phase 30.1.4)
 * — this hook depends on that endpoint change.
 */

import { useQuery } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';

export type InFlightStatus =
  | 'pending'
  | 'pulling_video'
  | 'analyzing'
  | 'pending_review'
  | 'pending_section_review';

export interface InFlightTrigger {
  id: string;
  status: InFlightStatus;
  triggerType: string;
  triggeredAt: string;
  startTime: string;
  endTime: string;
  cameraId: string | null;
  cameraName: string | null;
  sopId: string | null;
  observationSummary?: string | null;
}

interface InFlightTriggersResponse {
  data: InFlightTrigger[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const IN_FLIGHT_STATUSES = [
  'pending',
  'pulling_video',
  'analyzing',
  'pending_review',
  'pending_section_review',
].join(',');

export function useInFlightTriggers() {
  const api = useApi();

  return useQuery({
    queryKey: ['triggers', 'in-flight'] as const,
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('status', IN_FLIGHT_STATUSES);
      params.set('limit', '20');
      // Backend endpoint: GET /api/assessments/triggers (Phase 30.1.4 added the ?status= filter)
      return api.get<InFlightTriggersResponse>(`/api/assessments/triggers?${params}`);
    },
    // Adaptive: poll fast when there's work, slow when idle.
    // Mirrors the useTriggerDetail pattern at useAssessments.ts:207-211 (string-extracted
    // for readability — same idea, different shape: full list vs single record).
    refetchInterval: (query) => {
      const data = query.state.data as InFlightTriggersResponse | undefined;
      const hasInFlight = (data?.data?.length ?? 0) > 0;
      return hasInFlight ? 5000 : 30000;
    },
    refetchIntervalInBackground: false,
  });
}
